import type {
	UploadFileInfo,
	UploadOnAddEvent,
	UploadOnRemoveEvent,
} from "@tql/ui-components-react/kendo/upload";
import { useSetAtom } from "jotai";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import * as XLSX from "xlsx";
import { z } from "zod";

import { routes } from "~/app/routes/web-routes.ts";
import { QuoteDetailsContext } from "~/features/quote/context/quote-details-context.tsx";
import { useQuoteDetailsPageLoadQueries } from "~/features/quote/hooks/use-quote-details-hooks.ts";
import { useDeploymentType } from "~/shared/hooks/use-deployment-type.ts";
import { useFeatureFlagGate } from "~/shared/hooks/use-feature-flag-gate.ts";
import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import { postAndValidate } from "~/shared/services/api/fetch-client.ts";
import { sharedUserFilterAtom } from "~/shared/state/user-filter-state.ts";
import type { ApiResult } from "~/shared/types/api-types.ts";
import { ddUseLegacyBulkQuoteTemplate } from "~/shared/utils/datadog-actions.ts";
import { useIsAuthenticated } from "~/shared/utils/use-is-authenticated.tsx";

import {
	EXCEPTION_FAILURE_MESSAGE,
	MAX_EXCEL_ROWS,
} from "../constants/bulk-upload-constants.ts";
import type { BulkUploadResponse } from "../schemas/bulk-upload-schema.ts";
import {
	bulkUploadResponseSchema,
	LEGACY_BULK_UPLOAD_TEMPLATE_VERSION,
} from "../schemas/bulk-upload-schema.ts";
import type {
	BulkUploadQuote,
	BulkUploadRequest,
	BulkUploadTemplateType,
	CarrierPreference,
	LegacyBulkUploadRequest,
	PageData,
	RowValue,
} from "../types/bulk-upload-types.ts";
import type { BulkUploadWorkbookLayout } from "../utils/bulk-upload-utils.ts";
import {
	isBlankBulkUploadRow,
	mapRowToQuote,
	normalizeBulkUploadHeaders,
	resolveBulkUploadWorkbookLayout,
	trimTrailingBlankBulkUploadRows,
	validateHeadersAndFields,
} from "../utils/bulk-upload-utils.ts";

type BulkUploadSubmissionResult =
	| {
			status: "success";
			title: undefined;
			message: string;
			bulkQuoteId: string | null;
			templateType: BulkUploadTemplateType;
	  }
	| {
			status: "error";
			title: string;
			message: string;
	  };

export type UseBulkUploadResult = {
	files: UploadFileInfo[];
	errors: string[];
	isUploading: boolean;
	isLoading: boolean;
	isInternal: boolean;
	templateType: BulkUploadTemplateType | null;
	carrierPreference: CarrierPreference | undefined;
	isLookupDataReady: boolean;
	submissionResult: BulkUploadSubmissionResult | null;
	handleAdd: (event: UploadOnAddEvent) => void;
	handleRemove: (event: UploadOnRemoveEvent) => void;
	handleCarrierPreferenceChange: (value: CarrierPreference) => void;
	uploadFile: () => Promise<void>;
	confirmSubmissionSuccess: () => void;
	dismissSubmissionResult: () => void;
};

const buildFailureMessage = (errorMessage?: string) => {
	if (!errorMessage) {
		return "Failed to upload bulk quotes.";
	}

	return `Failed to upload bulk quotes. ${errorMessage}`;
};

const BULK_TENDER_DISABLED_MESSAGE = "Bulk Tender is not currently available.";

const getUploadPreconditionError = (
	fileCount: number,
	templateType: BulkUploadTemplateType | null,
	carrierPreference: CarrierPreference | undefined,
	isBulkTenderAvailable: boolean,
): string | null => {
	if (fileCount === 0) return "Please select a file first.";
	if (!templateType) {
		return "Could not determine whether this is a Quote or Tender file.";
	}
	if (templateType === "tender" && !isBulkTenderAvailable) {
		return BULK_TENDER_DISABLED_MESSAGE;
	}
	if (templateType === "tender" && !carrierPreference) {
		return "Please select carrier criteria.";
	}
	return null;
};

const buildSuccessSubmissionResult = (
	templateType: BulkUploadTemplateType,
	bulkQuoteId: string | null,
): BulkUploadSubmissionResult => ({
	status: "success",
	title: undefined,
	message:
		templateType === "tender"
			? "Bulk tender uploaded successfully!"
			: "Bulk quotes uploaded successfully!",
	bulkQuoteId,
	templateType,
});

const submitBulkTenderUpload = async (
	requestBody: BulkUploadRequest,
): Promise<ApiResult<BulkUploadResponse>> => {
	return await postAndValidate(
		apiRoutes.BULKTENDERUPLOAD,
		requestBody,
		bulkUploadResponseSchema,
	);
};

const submitLegacyBulkUpload = async (
	requestBody: LegacyBulkUploadRequest,
): Promise<ApiResult<null>> => {
	const result = await postAndValidate(
		apiRoutes.BULKUPLOAD,
		requestBody,
		z.unknown(),
	);

	return result.success ? { success: true, data: null } : result;
};

const buildBulkTenderRequest = (
	quotes: BulkUploadQuote[],
	layout: BulkUploadWorkbookLayout,
	carrierPreference: CarrierPreference | undefined,
	isInternal: boolean,
): BulkUploadRequest => {
	const request: BulkUploadRequest = {
		templateType: layout.templateType,
		quotes,
		isCustomerQuote: !isInternal,
		templateVersion:
			layout.templateVersion ?? LEGACY_BULK_UPLOAD_TEMPLATE_VERSION,
	};

	if (layout.templateType === "tender") {
		request.carrierPreference = carrierPreference;
	}

	return request;
};

const submitBulkUpload = async (
	quotes: BulkUploadQuote[],
	layout: BulkUploadWorkbookLayout,
	carrierPreference: CarrierPreference | undefined,
	isInternal: boolean,
	isBulkTenderAvailable: boolean,
): Promise<ApiResult<BulkUploadResponse | null>> => {
	if (!isBulkTenderAvailable) {
		return await submitLegacyBulkUpload({ quotes });
	}

	return await submitBulkTenderUpload(
		buildBulkTenderRequest(quotes, layout, carrierPreference, isInternal),
	);
};

type ParsedBulkUploadWorkbook =
	| {
			success: true;
			data: {
				layout: BulkUploadWorkbookLayout;
				headers: string[];
				rows: RowValue[][];
			};
	  }
	| { success: false; error: string };

const EMPTY_DOCUMENT_MESSAGE =
	"Document is empty or shipment data is missing. Please correct and resubmit.";

const parseBulkUploadWorkbook = (
	arrayBuffer: ArrayBuffer,
): ParsedBulkUploadWorkbook => {
	const workbook = XLSX.read(arrayBuffer, { type: "array" });
	const layoutResult = resolveBulkUploadWorkbookLayout(workbook);
	if (!layoutResult.success) return layoutResult;

	const sheetName = workbook.SheetNames[0];
	const worksheet = sheetName ? workbook.Sheets[sheetName] : undefined;
	if (!worksheet) {
		return { success: false, error: "Could not read spreadsheet data." };
	}

	const jsonData = XLSX.utils.sheet_to_json<RowValue[]>(worksheet, {
		header: 1,
	});
	const layout = layoutResult.data;
	const dataStartRowIndex = layout.dataStartRow - 1;
	if (jsonData.length <= dataStartRowIndex) {
		return { success: false, error: EMPTY_DOCUMENT_MESSAGE };
	}

	const headersRaw = jsonData[layout.headerRow - 1];
	const headers = Array.isArray(headersRaw)
		? normalizeBulkUploadHeaders(headersRaw.map(String))
		: [];
	// Blank rows are kept in place so every "Row N" message lines up with the
	// worksheet; validation and mapping skip them.
	const dataRows = trimTrailingBlankBulkUploadRows(
		jsonData.slice(dataStartRowIndex),
	);
	const populatedRowCount = dataRows.filter(
		(row) => !isBlankBulkUploadRow(row),
	).length;
	if (populatedRowCount === 0) {
		return { success: false, error: EMPTY_DOCUMENT_MESSAGE };
	}
	if (populatedRowCount > MAX_EXCEL_ROWS) {
		return {
			success: false,
			error: `Document contains more than ${MAX_EXCEL_ROWS} shipments. Please correct and resubmit.`,
		};
	}

	return { success: true, data: { layout, headers, rows: dataRows } };
};

const DATA_LOAD_ERROR_MESSAGE =
	"Error: Data load failed. Please contact your TQL Rep if this continues.";

const getLookupDataErrors = (pageData?: PageData | null): string[] => {
	const errors: string[] = [];

	if (!pageData) {
		errors.push(
			"Accessorials " + DATA_LOAD_ERROR_MESSAGE,
			"UnitTypes " + DATA_LOAD_ERROR_MESSAGE,
			"Freight Classes " + DATA_LOAD_ERROR_MESSAGE,
		);
		return errors;
	}

	const allAccs = [
		...Object.values(pageData.accessorials.selectable).flat(),
		...Object.values(pageData.accessorials.autoApply).flat(),
		...Object.values(pageData.accessorials.locationType).flat(),
	];

	if (allAccs.length === 0)
		errors.push("Accessorials " + DATA_LOAD_ERROR_MESSAGE);
	if (pageData.unitTypes.length === 0) {
		errors.push("UnitTypes " + DATA_LOAD_ERROR_MESSAGE);
	}
	if (pageData.freightClassData.length === 0) {
		errors.push("Freight Classes " + DATA_LOAD_ERROR_MESSAGE);
	}

	return errors;
};

export const useBulkUpload = (
	toggleBulkUploadModal: () => void,
): UseBulkUploadResult => {
	const navigate = useNavigate();
	const setSharedUserFilter = useSetAtom(sharedUserFilterAtom);
	const quoteContext = useContext(QuoteDetailsContext) as {
		quoteState: { customer?: { id: number; name?: string } };
	};
	const { quoteState } = quoteContext;
	const { isInternal } = useDeploymentType();
	const { isReady: isBulkTenderReady, isEnabled: isBulkTenderEnabled } =
		useFeatureFlagGate("traxLtlBulkTender");
	const isBulkTenderAvailable = isBulkTenderReady && isBulkTenderEnabled;
	const { email } = useIsAuthenticated();

	const [files, setFiles] = useState<UploadFileInfo[]>([]);
	const [errors, setErrors] = useState<string[]>([]);
	const [isUploading, setIsUploading] = useState(false);
	const [carrierPreference, setCarrierPreference] = useState<
		CarrierPreference | undefined
	>(undefined);
	const [templateType, setTemplateType] =
		useState<BulkUploadTemplateType | null>(null);
	const [submissionResult, setSubmissionResult] =
		useState<BulkUploadSubmissionResult | null>(null);
	const fileDetectionGeneration = useRef(0);

	const { data, isLoading } = useQuoteDetailsPageLoadQueries();
	const pageData = data as unknown as PageData | null | undefined;
	const lookupDataErrors = useMemo(
		() => (isLoading ? [] : getLookupDataErrors(pageData)),
		[isLoading, pageData],
	);
	const lookupDataErrorsKey = lookupDataErrors.join("|");
	const isLookupDataReady = !isLoading && lookupDataErrorsKey.length === 0;

	useEffect(() => {
		if (!isLoading && lookupDataErrorsKey.length > 0) {
			setErrors((previousErrors) => {
				if (previousErrors.join("|") === lookupDataErrorsKey) {
					return previousErrors;
				}
				return lookupDataErrors;
			});
		}
	}, [isLoading, lookupDataErrorsKey, lookupDataErrors]);

	const handleAdd = (event: UploadOnAddEvent) => {
		const detectionGeneration = ++fileDetectionGeneration.current;
		const selectedFile = event.newState.at(-1);
		setFiles(selectedFile ? [selectedFile] : []);
		setErrors([]);
		setCarrierPreference(undefined);
		setTemplateType(null);

		const file = selectedFile?.getRawFile?.();
		if (!file) return;

		void file
			.arrayBuffer()
			.then((arrayBuffer) => {
				if (detectionGeneration !== fileDetectionGeneration.current) return;
				const workbook = XLSX.read(arrayBuffer, { type: "array" });
				const layoutResult = resolveBulkUploadWorkbookLayout(workbook);
				if (!layoutResult.success) {
					setErrors([layoutResult.error]);
					return;
				}
				setTemplateType(layoutResult.data.templateType);
			})
			.catch(() => {
				if (detectionGeneration !== fileDetectionGeneration.current) return;
				setErrors(["Could not read selected file."]);
			});
	};

	const handleRemove = (event: UploadOnRemoveEvent) => {
		fileDetectionGeneration.current++;
		setFiles(event.newState);
		if (event.newState.length === 0) {
			setErrors([]);
			setCarrierPreference(undefined);
			setTemplateType(null);
		}
	};

	const handleCarrierPreferenceChange = (value: CarrierPreference) => {
		setCarrierPreference(value);
	};

	const confirmSubmissionSuccess = () => {
		const customer = quoteState?.customer;
		const destination =
			submissionResult?.status === "success" && submissionResult.bulkQuoteId
				? `${routes.quotes}?${new URLSearchParams({
						quoteType: "Bulk",
						focusBatch: submissionResult.bulkQuoteId,
					}).toString()}`
				: routes.quotes;

		// Bulk-uploaded quotes are owned by the customer's LTL rep rather than the
		// uploader, so scope the board by customer or the new batch is filtered out.
		if (customer?.id != null) {
			setSharedUserFilter({
				paramType: "customers",
				value: [
					{ id: customer.id, name: customer.name ?? String(customer.id) },
				],
			});
		}

		setSubmissionResult(null);
		toggleBulkUploadModal();
		navigate(destination);
	};

	const dismissSubmissionResult = () => {
		setSubmissionResult(null);
	};

	const uploadFile = async () => {
		const preconditionError = getUploadPreconditionError(
			files.length,
			templateType,
			carrierPreference,
			isBulkTenderAvailable,
		);
		if (preconditionError) {
			setErrors([preconditionError]);
			return;
		}
		if (!templateType) return;

		if (isLoading) {
			setErrors([
				"Reference data is still loading. Please wait and try again.",
			]);
			return;
		}

		if (lookupDataErrors.length > 0) {
			setErrors(lookupDataErrors);
			return;
		}

		setIsUploading(true);
		try {
			const firstFile = files[0];
			const file = firstFile?.getRawFile ? firstFile.getRawFile() : null;

			if (!file) {
				setErrors(["Could not read selected file."]);
				setIsUploading(false);
				return;
			}

			const arrayBuffer = await file.arrayBuffer();
			const workbookResult = parseBulkUploadWorkbook(arrayBuffer);
			if (!workbookResult.success) {
				setErrors([workbookResult.error]);
				setIsUploading(false);
				return;
			}
			const { layout, headers, rows } = workbookResult.data;
			const parsedPreconditionError = getUploadPreconditionError(
				files.length,
				layout.templateType,
				carrierPreference,
				isBulkTenderAvailable,
			);
			if (parsedPreconditionError) {
				setErrors([parsedPreconditionError]);
				return;
			}

			const validationErrors = validateHeadersAndFields(
				headers,
				rows,
				pageData?.holidays,
				layout.templateType,
				pageData,
				layout.dataStartRow,
			);
			if (validationErrors.length > 0) {
				setErrors(validationErrors);
				setIsUploading(false);
				return;
			}

			const quotes = rows
				.filter((row) => !isBlankBulkUploadRow(row))
				.map((row) =>
					mapRowToQuote(
						row,
						headers,
						pageData,
						quoteState,
						isInternal,
						typeof email === "string" ? email : "",
						layout.templateType,
					),
				);
			if (layout.isLegacyQuote) {
				ddUseLegacyBulkQuoteTemplate();
			}
			const result = await submitBulkUpload(
				quotes,
				layout,
				carrierPreference,
				isInternal,
				isBulkTenderAvailable,
			);

			if (result.success) {
				setSubmissionResult(
					buildSuccessSubmissionResult(
						layout.templateType,
						result.data?.bulkQuoteId ?? null,
					),
				);
			} else {
				setSubmissionResult({
					status: "error",
					title: "Bulk Upload Failed",
					message: buildFailureMessage(result.error),
				});
			}
		} catch (error) {
			console.error(error);
			setSubmissionResult({
				status: "error",
				title: "Bulk Upload Failed",
				message: EXCEPTION_FAILURE_MESSAGE,
			});
		} finally {
			setIsUploading(false);
		}
	};

	return {
		files,
		errors,
		isUploading,
		isLoading,
		isInternal,
		templateType,
		carrierPreference,
		isLookupDataReady,
		submissionResult,
		handleAdd,
		handleRemove,
		handleCarrierPreferenceChange,
		uploadFile,
		confirmSubmissionSuccess,
		dismissSubmissionResult,
	};
};
